import type { BoardAutomationSettings, BoardCardField, BoardColumnSettings, TaskAutomationEvent, Todo } from "../../domain/entities.ts";

export const DEFAULT_BOARD_AUTOMATION: BoardAutomationSettings = { completedColumn: null, overdueUrgent: false, completeWithSubtasks: false };

export function normalizeBoardAutomation(columns: string[], value?: Partial<BoardAutomationSettings>): BoardAutomationSettings {
  return {
    completedColumn: value?.completedColumn && columns.includes(value.completedColumn) ? value.completedColumn : null,
    overdueUrgent: !!value?.overdueUrgent,
    completeWithSubtasks: !!value?.completeWithSubtasks,
  };
}

export function applyBoardAutomations(todo: Todo, previous: Todo | undefined, columns: string[], settings: BoardAutomationSettings | undefined, date: string, now: number): TaskAutomationEvent[] {
  if (!settings || todo.deleted || todo.archivedAt) return [];
  const events: TaskAutomationEvent[] = [];
  const record = (rule: TaskAutomationEvent["rule"]) => events.push({ id: `${rule}:${now}`, rule, at: now });
  if (settings.completeWithSubtasks && !todo.completed && !!todo.subtasks?.length && todo.subtasks.every(item => item.completed)) {
    todo.completed = true; todo.completedAt = now; record("subtasks-complete");
  }
  if (settings.completedColumn && todo.completed && !previous?.completed) {
    for (const column of columns) if (column !== settings.completedColumn && todo.tags[column]) todo.tags[column] = false;
    todo.tags[settings.completedColumn] = true;
    todo.tagOrder = [settings.completedColumn, ...(todo.tagOrder || []).filter(tag => tag !== settings.completedColumn && !columns.includes(tag))];
    record("completed-column");
  }
  if (settings.overdueUrgent && !todo.completed && !!todo.dueDate && todo.dueDate < date && todo.priority !== "urgent") {
    todo.priority = "urgent"; record("overdue-urgent");
  }
  if (events.length) todo.automationEvents = [...events, ...(todo.automationEvents || [])].slice(0, 20);
  return events;
}

export function normalizeBoardColumnSettings(columns: string[], settings: Record<string, Partial<BoardColumnSettings> | undefined> = {}): Record<string, BoardColumnSettings> {
  return Object.fromEntries(columns.flatMap(column => {
    const value = settings[column];
    if (!value) return [];
    const normalized: BoardColumnSettings = {
      defaultPriority: value.defaultPriority || "none",
      defaultAssigneeId: value.defaultAssigneeId || null,
      defaultDueOffsetDays: Number.isFinite(value.defaultDueOffsetDays) && value.defaultDueOffsetDays! >= 0 ? Math.floor(value.defaultDueOffsetDays!) : null,
      completeOnEnter: !!value.completeOnEnter,
      reopenOnLeave: !!value.reopenOnLeave,
    };
    const configured = normalized.defaultPriority !== "none" || !!normalized.defaultAssigneeId || normalized.defaultDueOffsetDays !== null || normalized.completeOnEnter || normalized.reopenOnLeave;
    return configured ? [[column, normalized]] : [];
  }));
}

export function normalizeBoardCardFields(fields: string[] = []): BoardCardField[] {
  const allowed = new Set<BoardCardField>(["assignee", "due", "priority", "tags", "subtasks", "comments", "recurrence", "updated"]);
  return [...new Set(fields)].filter((field): field is BoardCardField => allowed.has(field as BoardCardField)).slice(0, 3);
}

export function transitionedCompletion(completed: boolean, source?: BoardColumnSettings, destination?: BoardColumnSettings): boolean {
  if (destination?.completeOnEnter) return true;
  if (source?.reopenOnLeave) return false;
  return completed;
}

export function boardCardFields(fields: BoardCardField[] | undefined): BoardCardField[] {
  return normalizeBoardCardFields(fields === undefined ? ["due", "priority", "comments"] : fields);
}

export function applyColumnDefaults(todo: Todo, settings: BoardColumnSettings | undefined, dueDate: (days: number) => string, allowCompletion = true): void {
  if (!settings) return;
  if (settings.defaultPriority && settings.defaultPriority !== "none") todo.priority = settings.defaultPriority;
  if (settings.defaultAssigneeId) todo.assignedTo = [...new Set([...(todo.assignedTo || []), settings.defaultAssigneeId])];
  if (settings.defaultDueOffsetDays !== null && settings.defaultDueOffsetDays !== undefined) todo.dueDate = dueDate(settings.defaultDueOffsetDays);
  if (settings.completeOnEnter && allowCompletion) { todo.completed = true; todo.completedAt = Date.now(); }
}
